import { useEffect, useState } from "react";

// First-touch UTM attribution. Captures utm_* params from the URL and stores
// them (once) so registration can report which blog/post drove the signup.
const KEY = "owp_utm";

export function captureUtm() {
  try {
    const p = new URLSearchParams(window.location.search);
    const utm = {};
    ["source", "medium", "campaign"].forEach((k) => {
      const v = p.get(`utm_${k}`);
      if (v) utm[k] = v;
    });
    if (Object.keys(utm).length && !localStorage.getItem(KEY)) {
      localStorage.setItem(KEY, JSON.stringify(utm));
    }
  } catch {}
}

export function getUtm() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Build the query string for the server-side Google OAuth redirect.
export function utmQuery() {
  const u = getUtm();
  if (!u) return "";
  const p = new URLSearchParams();
  ["source", "medium", "campaign"].forEach((k) => {
    if (u[k]) p.set(`utm_${k}`, u[k]);
  });
  const s = p.toString();
  return s ? `?${s}` : "";
}

// --- Outbound link tagging -------------------------------------------------
// Append UTM params to a URL we share out, so traffic (and signups) can be
// attributed back to the channel/campaign that sent it.
export function withUtm(url, { source, medium = "social", campaign = "share" } = {}) {
  if (!url) return url;
  try {
    const u = new URL(url);
    if (source) u.searchParams.set("utm_source", source);
    if (medium) u.searchParams.set("utm_medium", medium);
    if (campaign) u.searchParams.set("utm_campaign", campaign);
    return u.toString();
  } catch {
    return url;
  }
}

const NET = { x: "twitter", fb: "facebook", wa: "whatsapp", li: "linkedin" };

// Returns a ready share-intent URL for a social network, with the target link
// UTM-tagged (source = network, medium = social, campaign = provided). A CTA line
// is appended to the shared text so readers know to tap through.
export function shareIntent(net, url, hook, campaign = "share", cta = "Read the full study:") {
  const tagged = encodeURIComponent(withUtm(url, { source: NET[net] || net, medium: "social", campaign }));
  const body = [hook, cta].filter(Boolean).join("\n\n");
  const text = encodeURIComponent(body || "From OpenWord AI");
  if (net === "x") return `https://twitter.com/intent/tweet?text=${text}&url=${tagged}`;
  if (net === "fb") return `https://www.facebook.com/sharer/sharer.php?u=${tagged}`;
  if (net === "li") return `https://www.linkedin.com/sharing/share-offsite/?url=${tagged}`;
  if (net === "wa") return `https://wa.me/?text=${text}%20${tagged}`;
  return "#";
}

// Full caption (hook + CTA + tagged link) for platforms with no web-share intent
// (e.g. Instagram) — the user copies this and pastes it manually.
export function shareCaption(url, hook, campaign = "share", cta = "Read the full study:") {
  const tagged = withUtm(url, { source: "instagram", medium: "social", campaign });
  return [hook, cta, tagged].filter(Boolean).join("\n\n");
}

// A copy-paste link, tagged so pasted-anywhere shares still attribute.
export function taggedLink(url, campaign = "share") {
  return withUtm(url, { source: "share", medium: "link", campaign });
}

// A ready mailto: link (works on desktop + mobile) with the hook + tagged link
// in the body, so email shares attribute back like the social ones.
export function emailShare(url, hook, subject = "From OpenWord AI", campaign = "share") {
  const link = withUtm(url, { source: "email", medium: "email", campaign });
  const body = encodeURIComponent(`${hook || "Thought you'd appreciate this from OpenWord AI."}\n\n${link}`);
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${body}`;
}

// --- Short.io-backed tracked links -----------------------------------------
// Every outbound share (copy link, social intent, email) is upgraded to a short, click- and
// geo-tracked link via POST /api/links/share-links (Short.io when configured, else the app's
// own internal shortener — either way the backend decides). The sync request happens once the
// dialog opens; the untagged/long-URL helpers above remain the instant fallback while it loads
// or if it fails, so nothing ever renders blank.
export function useTrackedLinks(url, campaign = "share") {
  const [tracked, setTracked] = useState(null);
  useEffect(() => {
    setTracked(null);
    if (!url) return;
    let alive = true;
    (async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API}/api/links/share-links`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ url, campaign }),
        });
        if (res.ok && alive) setTracked(await res.json());
      } catch { /* keep using the long-URL fallback */ }
    })();
    return () => { alive = false; };
  }, [url, campaign]);
  return tracked;
}

const _NET_KEY = { x: "x", fb: "facebook", wa: "whatsapp", li: "linkedin" };

// Social share-intent href, using the pre-tracked short link when ready, falling back to the
// long UTM-tagged URL otherwise.
export function trackedIntent(net, tracked, url, hook, campaign = "share", cta = "Read the full study:") {
  const short = tracked?.[_NET_KEY[net]];
  if (!short) return shareIntent(net, url, hook, campaign, cta);
  const body = [hook, cta].filter(Boolean).join("\n\n");
  const text = encodeURIComponent(body || "From OpenWord AI");
  const u = encodeURIComponent(short);
  if (net === "x") return `https://twitter.com/intent/tweet?text=${text}&url=${u}`;
  if (net === "fb") return `https://www.facebook.com/sharer/sharer.php?u=${u}`;
  if (net === "li") return `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
  if (net === "wa") return `https://wa.me/?text=${text}%20${u}`;
  return "#";
}

// Copy-link target, preferring the tracked short link.
export function trackedCopyLink(tracked, url, campaign = "share") {
  return tracked?.copy || taggedLink(url, campaign);
}

// Instagram-style caption (hook + CTA + tracked short link), for copy/paste sharing.
export function trackedCaption(tracked, url, hook, campaign = "share", cta = "Read the full study:") {
  if (!tracked?.copy) return shareCaption(url, hook, campaign, cta);
  return [hook, cta, tracked.copy].filter(Boolean).join("\n\n");
}

// mailto: href built from the tracked short link when ready; `lines` are the body lines shown
// before the link (a single string or an array of strings).
export function trackedEmailHref(tracked, url, lines, subject = "From OpenWord AI", campaign = "share") {
  const link = tracked?.email || withUtm(url, { source: "email", medium: "email", campaign });
  const body = [...(Array.isArray(lines) ? lines : [lines]), link].filter(Boolean).join("\n\n");
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}
